import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { audienceModeService } from '@/services/audienceModeService';
import { Users, MapPin, Tv, Radio, Loader2, TrendingUp, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface AudienceStatsProps {
  gameId: string;
}

export const AudienceStats: React.FC<AudienceStatsProps> = ({ gameId }) => {
  const [stats, setStats] = useState<{
    total: number;
    onsite: number;
    watching: number;
    listening: number;
    byOutlet: Record<string, number>;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 10000); // Refresh every 10 seconds

    // Listen for real-time audience updates
    const handleSessionStarted = (e: CustomEvent) => {
      if (e.detail.gameId === gameId) {
        loadStats();
        toast.success('New viewer joined!', {
          description: 'Audience count updated',
          duration: 2000
        });
      }
    };

    const handleSessionEnded = () => {
      loadStats();
    };

    window.addEventListener('audience-session-started', handleSessionStarted as EventListener);
    window.addEventListener('audience-session-ended', handleSessionEnded);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('audience-session-started', handleSessionStarted as EventListener);
      window.removeEventListener('audience-session-ended', handleSessionEnded);
    };
  }, [gameId]);

  const loadStats = async () => {
    try {
      const data = await audienceModeService.getSessionStats(gameId);
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!stats || stats.total === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Audience Stats
          </CardTitle>
          <CardDescription>No active audience sessions yet</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const getPercentage = (value: number) => {
    return stats.total > 0 ? Math.round((value / stats.total) * 100) : 0;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <CardTitle>Live Audience</CardTitle>
                <CardDescription>Real-time viewing statistics</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-muted-foreground animate-pulse" />
              <Badge variant="default" className="text-lg px-4 py-2">
                {stats.total} Active
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-accent/50">
              <MapPin className="w-8 h-8 text-primary" />
              <div className="flex-1">
                <div className="text-2xl font-bold">{stats.onsite}</div>
                <div className="text-sm text-muted-foreground">At Venue</div>
                <Progress value={getPercentage(stats.onsite)} className="mt-2" />
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 rounded-lg bg-accent/50">
              <Tv className="w-8 h-8 text-primary" />
              <div className="flex-1">
                <div className="text-2xl font-bold">{stats.watching}</div>
                <div className="text-sm text-muted-foreground">Watching TV</div>
                <Progress value={getPercentage(stats.watching)} className="mt-2" />
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 rounded-lg bg-accent/50">
              <Radio className="w-8 h-8 text-primary" />
              <div className="flex-1">
                <div className="text-2xl font-bold">{stats.listening}</div>
                <div className="text-sm text-muted-foreground">Listening Radio</div>
                <Progress value={getPercentage(stats.listening)} className="mt-2" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {Object.keys(stats.byOutlet).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Popular Outlets</CardTitle>
            <CardDescription>Most viewed channels and stations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.byOutlet)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([outlet, count]) => (
                  <div
                    key={outlet}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <span className="font-medium">{outlet}</span>
                    <Badge variant="secondary">{count} viewer{count !== 1 ? 's' : ''}</Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
